import { supabase } from './supabase';
import { useHospital } from './hooks/useHospital';
import { AuthError } from '@supabase/supabase-js';

/**
 * Link profile to hospital from signup metadata (admin-created staff/doctors).
 */
export async function syncHospitalIdFromMetadata(): Promise<string | null> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return null;
    }

    const metaHospitalId = user.user_metadata?.hospital_id as string | undefined;
    if (!metaHospitalId) {
      return null;
    }

    const { data: profile } = await supabase
      .from('profiles')
      .select('hospital_id')
      .eq('id', user.id)
      .single();

    if (profile?.hospital_id) {
      return profile.hospital_id;
    }

    const { error: updateError } = await supabase
      .from('profiles')
      .update({ hospital_id: metaHospitalId })
      .eq('id', user.id);

    if (updateError) {
      console.error('Could not sync hospital_id from metadata:', updateError);
      return null;
    }

    return metaHospitalId;
  } catch (error) {
    console.error('Error syncing hospital from metadata:', error);
    return null;
  }
}

/**
 * Get the current hospital ID from the user's profile
 */
export async function getCurrentHospitalId(): Promise<string | null> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return null;
    }

    // Get user's profile to find their hospital_id and role
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('hospital_id, role')
      .eq('id', user.id)
      .single();

    // Super admins don't have a hospital_id - this is expected
    if (profile?.role === 'super_admin') {
      return null;
    }

    if (profile?.hospital_id) {
      return profile.hospital_id;
    }

    // Admin-created users may have hospital_id only in auth metadata until profile is updated
    const syncedId = await syncHospitalIdFromMetadata();
    if (syncedId) {
      return syncedId;
    }

    if (error) {
      console.error('Error fetching user hospital:', error);
    }

    return null;
  } catch (error) {
    console.error('Error getting current hospital:', error);
    return null;
  }
}

/**
 * Get all hospitals for a user (in case they have access to multiple)
 */
export async function getUserHospitals(): Promise<any[]> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return [];
    }

    // For now, get the hospital from the user's profile
    const { data: profile } = await supabase
      .from('profiles')
      .select('hospital_id')
      .eq('id', user.id)
      .single();

    if (!profile?.hospital_id) {
      return [];
    }

    // Get hospital details - only select needed fields for faster query
    // Note: hospitals table has 'id', 'name', 'slug' - NOT 'hospital_id'
    const { data: hospital } = await supabase
      .from('hospitals')
      .select('id, name, slug')
      .eq('id', profile.hospital_id)
      .single();

    return hospital ? [hospital] : [];
  } catch (error) {
    console.error('Error getting user hospitals:', error);
    return [];
  }
}

/**
 * Hospital ID for writes (patients, master_patients, etc.).
 * Uses profile.hospital_id — not the optional persisted useHospital store.
 */
export async function requireHospitalIdForWrite(): Promise<string> {
  const hospitalId = await getCurrentHospitalId();
  if (!hospitalId) {
    throw new Error(
      'Your account is not linked to a hospital. Please log out, log in again, or ask your administrator to assign your hospital.'
    );
  }

  const hospitals = await getUserHospitals();
  if (hospitals.length > 0) {
    const { currentHospital, setHospital, clearHospital } = useHospital.getState();
    if (!currentHospital || currentHospital.id !== hospitalId) {
      if (currentHospital && currentHospital.id !== hospitalId) {
        clearHospital();
      }
      setHospital(hospitals[0]);
    }
  }

  return hospitalId;
}

/**
 * Initialize hospital context after login
 */
export async function initializeHospitalContext() {
  try {
    await requireHospitalIdForWrite();
  } catch {
    useHospital.getState().clearHospital();
  }
}

/**
 * Middleware to ensure hospital context is set
 */
export async function requireHospitalContext(): Promise<string> {
  const hospitalId = await getCurrentHospitalId();

  if (!hospitalId) {
    throw new Error('No hospital context found. Please login.');
  }

  return hospitalId;
}

/**
 * Add hospital_id filter to a query automatically
 */
export function addHospitalFilter<T>(
  query: any,
  hospitalId: string | null
): any {
  if (!hospitalId) {
    return query;
  }

  return query.eq('hospital_id', hospitalId);
}

